/**
 * Agency router — the sports-agent persona's roster surface.
 *
 * An agent's roster is an `Organization` with `type='agency'`: the agent
 * is a Member in role 'owner' (or 'admin' for Team-plan seat-mates) and
 * each rostered athlete is a Member in role 'member'. Modelling it this
 * way means the entire invite machinery already shipped in
 * `organization.router.ts` — stub-User creation for brand-new emails,
 * magic-link invitation email, the resend decision tree, accept-invite —
 * works for agency invites with no new code.
 *
 * So this router deliberately does NOT reimplement invite/remove/roles.
 * The roster UI calls `organization.invite` / `organization.removeMember`
 * / `organization.updateMemberRole` directly with the agency's orgId.
 * What lives here is the thing those endpoints can't express: a roster
 * view that joins each athlete to their deal activity.
 *
 * Authorization follows the `compliance.router.ts` pattern — resolve the
 * caller's agency org ids in-handler, then scope every read to athletes
 * who are members of one of them. A caller with no agency membership
 * gets empty results, never another agency's roster.
 *
 * BILLING (founder ruling 2026-06-20): roster management is FREE. There
 * is no `requireAgentPlan` gate on this router. The paid unlock is the
 * usefoundry recruiting dataset, which lands in a later PR and will gate
 * only its own endpoints. Keeping the roster open is deliberate —
 * top-of-funnel adoption over early monetization.
 *
 * @package @app/api
 */

// TYPE-ONLY import — the VALUE import happens dynamically inside each
// handler, same convention as `await import('@app/db')` everywhere in the
// routers: the router module is reachable from the client bundle via the
// loaders' api-server chain, and a static value import would drag the
// provider package (and through its cache, ioredis + node builtins) into
// the browser build.
import type { FoundryAthlete } from '@app/foundry-athletes';
import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { writeAuditLog } from '../lib/audit';
import {
  commissionEarned,
  createDealCore,
  dealCreateInput,
  isAwaitingAthleteConfirmation,
  isWithinContractWindow,
  resolveCommissionPercent,
} from '../lib/deal-core';
import { inviteMemberCore } from '../lib/org-invite';
import { buildProspectMergePatch } from '../lib/prospect-merge';
import { protectedProcedure } from '../middleware/auth';
import { requireFeature } from '../middleware/feature-gate';

/** Roles that can administer an agency roster. */
const AGENCY_STAFF_ROLES = ['owner', 'admin'] as const;

/**
 * Soft roster caps (founder ruling 2026-06-20): these drive an upsell
 * banner, they do NOT block. Hard enforcement is deliberately deferred —
 * the last thing we want is a wall when a pilot agent signs athlete #11.
 */
export const ROSTER_SOFT_CAPS = {
  free: 3,
  'agent-solo': 10,
  'agent-team': 50,
} as const;

/** Agency orgs the caller can administer, with the caller's role. */
async function callerAgencyOrgs(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any,
  userId: string,
): Promise<Array<{ id: string; name: string; slug: string; role: string }>> {
  const rows = await db.member.findMany({
    where: {
      userId,
      role: { in: AGENCY_STAFF_ROLES as unknown as string[] },
      organization: { type: 'agency' },
    },
    include: { organization: { select: { id: true, name: true, slug: true } } },
    orderBy: { createdAt: 'asc' },
  });
  // biome-ignore lint/suspicious/noExplicitAny: prisma row shape
  return rows.map((m: any) => ({
    id: m.organization.id,
    name: m.organization.name,
    slug: m.organization.slug,
    role: m.role,
  }));
}

/**
 * Assert the caller administers `orgId` and that it is an agency.
 * Returns the org row. Throws FORBIDDEN otherwise — never leaks whether
 * the org exists.
 */
async function requireAgencyAccess(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any,
  userId: string,
  orgId: string,
): Promise<{ id: string; name: string; slug: string; role: string }> {
  const orgs = await callerAgencyOrgs(db, userId);
  const match = orgs.find((o) => o.id === orgId);
  if (!match) {
    throw new ORPCError('FORBIDDEN', {
      message: 'You do not administer this agency.',
    });
  }
  return match;
}

/**
 * Assert the caller administers `orgId` AND that `athleteId` is actually
 * on that roster. Returns the agency plus the athlete's Member row (which
 * carries the commission rate).
 *
 * This is the authorization boundary for every on-behalf operation — an
 * agent must never be able to act for someone who isn't their client.
 * Reads the membership through the ENHANCED client on purpose: it can
 * only ever see rows policy permits, so it cannot over-report the roster.
 */
async function requireRosteredAthlete(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any,
  callerId: string,
  orgId: string,
  athleteId: string,
): Promise<{
  agency: { id: string; name: string; slug: string; role: string };
  // commissionPercent is coerced from Prisma's Decimal here so every call
  // site works with a plain number and nothing leaks a Decimal across the
  // oRPC boundary.
  member: { id: string; commissionPercent: number | null; contractStartDate: Date | null };
}> {
  const agency = await requireAgencyAccess(db, callerId, orgId);
  const member = await db.member.findFirst({
    where: { organizationId: orgId, userId: athleteId, role: 'member' },
    select: { id: true, commissionPercent: true, contractStartDate: true },
  });
  if (!member) {
    throw new ORPCError('FORBIDDEN', {
      message: 'That athlete is not on your roster.',
    });
  }
  return {
    agency,
    member: {
      id: member.id,
      commissionPercent:
        member.commissionPercent === null || member.commissionPercent === undefined
          ? null
          : Number(member.commissionPercent),
      contractStartDate: member.contractStartDate ?? null,
    },
  };
}

/** Shape of a Member row joined to its User, as the roster query returns it. */
export interface RosterMemberRow {
  id: string;
  userId: string;
  createdAt: Date;
  commissionPercent?: unknown;
  contractStartDate?: Date | string | null;
  user: {
    id: string;
    name: string | null;
    email: string;
    image?: string | null;
    sport?: string | null;
    school?: string | null;
    classYear?: string | null;
    emailVerified?: boolean | null;
    ncaaId?: string | null;
  };
}

/**
 * A deal row as the roster query returns it.
 *
 * We fetch deals rather than a grouped aggregate because the contract
 * window is PER ATHLETE — a single `groupBy` can't express "count this
 * athlete's deals since their own contract start". Roster sizes are
 * 10-50, so this is not a scaling concern, and it keeps the windowing
 * rule in one unit-tested pure function instead of split across SQL.
 */
export interface RosterDealRow {
  athleteId: string;
  amount: unknown;
  commissionPercent?: unknown;
  signedDate?: Date | string | null;
  startDate?: Date | string | null;
  createdAt?: Date | string | null;
  status?: string | null;
}

/**
 * Pure shaping of roster rows — extracted from the handler so the
 * per-athlete join, the "Invited vs Active" derivation, and the portfolio
 * totals are unit-testable without a database.
 *
 * Athletes with no deals must still appear (an invited athlete who hasn't
 * added anything is exactly who the agent needs to chase), so the deal
 * aggregate is a left-join by way of a default rather than a filter.
 */
export function buildRosterView(members: RosterMemberRow[], deals: RosterDealRow[]) {
  const byAthlete = new Map<string, RosterDealRow[]>();
  for (const d of deals) {
    const list = byAthlete.get(d.athleteId);
    if (list) list.push(d);
    else byAthlete.set(d.athleteId, [d]);
  }

  const athletes = members.map((m) => {
    const memberRate =
      m.commissionPercent === null || m.commissionPercent === undefined
        ? null
        : Number(m.commissionPercent);
    const contractStart = m.contractStartDate ?? null;

    // Only deals struck within the representation window count toward the
    // agent's tracked value and commission (founder ruling 2026-07-27).
    const all = byAthlete.get(m.userId) ?? [];
    const counted = all.filter((d) => isWithinContractWindow(d, contractStart));

    const totalValue = counted.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const commission = counted.reduce(
      (sum, d) => sum + commissionEarned(Number(d.amount) || 0, d.commissionPercent as number, memberRate),
      0,
    );

    return {
      memberId: m.id,
      userId: m.user.id,
      name: m.user.name,
      email: m.user.email,
      image: m.user.image ?? null,
      sport: m.user.sport ?? null,
      school: m.user.school ?? null,
      classYear: m.user.classYear ?? null,
      hasNcaaId: Boolean(m.user.ncaaId),
      // Derived from auth state rather than a stored column, so it can
      // never drift out of sync with whether they actually signed in.
      status: m.user.emailVerified ? ('active' as const) : ('invited' as const),
      joinedAt: m.createdAt.toISOString(),
      commissionPercent: memberRate,
      contractStartDate: contractStart
        ? new Date(contractStart).toISOString()
        : null,
      // Counts reflect the window; `dealCountExcluded` explains any gap
      // rather than leaving the agent to wonder where deals went.
      dealCount: counted.length,
      dealCountExcluded: all.length - counted.length,
      totalValue,
      commissionEarned: commission,
    };
  });

  const totals = athletes.reduce(
    (acc, a) => ({
      athletes: acc.athletes + 1,
      deals: acc.deals + a.dealCount,
      value: acc.value + a.totalValue,
      commission: acc.commission + a.commissionEarned,
    }),
    { athletes: 0, deals: 0, value: 0, commission: 0 },
  );

  return { athletes, totals };
}

export const agencyRouter = {
  /**
   * Agency orgs the caller administers. Drives the /agency layout guard
   * and the org switcher when an agent runs more than one book.
   */
  myAgencies: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const agencies = await callerAgencyOrgs(db, session.user.id);
    return { agencies, isAgent: agencies.length > 0 };
  }),

  /**
   * The roster: every athlete member of the agency, joined to their deal
   * activity so the agent sees book value at a glance.
   *
   * "Invited vs Active" is derived, not stored — an athlete who hasn't
   * completed their magic-link sign-in yet has `emailVerified = false`.
   * That avoids a status column that could drift out of sync with the
   * actual auth state.
   */
  listRoster: protectedProcedure
    .input(z.object({ orgId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      // Authorization boundary: throws unless the caller administers this
      // agency. Runs on the ENHANCED client deliberately — it reads only
      // the caller's own membership rows, so ZenStack acts as
      // defense-in-depth and it can never over-report which agencies the
      // caller administers.
      const agency = await requireAgencyAccess(db, session.user.id, input.orgId);

      // Everything below is a CROSS-USER read and must use raw prisma.
      // The enhanced client enforces `User`'s row policy
      // (`@@allow('read', auth() == this)`), so the `include: { user }`
      // below silently returns nothing for every athlete who isn't the
      // caller — which presented as "roster shows zero athletes" even
      // though the Member rows existed and the athlete could see their
      // own membership. Same for the deal aggregate over other users'
      // deals. `requireAgencyAccess` above is the real gate.
      const { prisma: rawPrisma } = await import('@app/db');

      const members = await rawPrisma.member.findMany({
        where: { organizationId: input.orgId, role: 'member' },
        include: {
          user: {
            select: {
              id: true,
              name: true,
              email: true,
              image: true,
              sport: true,
              school: true,
              classYear: true,
              emailVerified: true,
              ncaaId: true,
            },
          },
        },
        orderBy: { createdAt: 'desc' },
      });

      const athleteIds = members.map((m: { userId: string }) => m.userId);

      // One query for the whole roster's deals — NOT a groupBy. The
      // contract window is per-athlete (each has their own start date),
      // which a single grouped aggregate can't express. Windowing then
      // happens in `buildRosterView`, the unit-tested pure function.
      // Cancelled deals are excluded; they'd inflate the roster totals.
      const deals =
        athleteIds.length > 0
          ? await rawPrisma.deal.findMany({
              where: { athleteId: { in: athleteIds }, status: { not: 'CANCELLED' } },
              select: {
                athleteId: true,
                amount: true,
                commissionPercent: true,
                signedDate: true,
                startDate: true,
                createdAt: true,
                status: true,
              },
            })
          : [];

      const { athletes, totals } = buildRosterView(members, deals);
      return { agency, athletes, totals };
    }),

  /**
   * One rostered athlete: profile summary, their deals, and the agency's
   * commission rate for them. Cross-user read → raw prisma, gated by
   * `requireRosteredAthlete`.
   */
  getAthlete: protectedProcedure
    .input(z.object({ orgId: z.string(), athleteId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const { member } = await requireRosteredAthlete(
        db,
        session.user.id,
        input.orgId,
        input.athleteId,
      );
      const { prisma: rawPrisma } = await import('@app/db');

      const [athlete, deals] = await Promise.all([
        rawPrisma.user.findUnique({
          where: { id: input.athleteId },
          select: {
            id: true,
            name: true,
            email: true,
            image: true,
            sport: true,
            school: true,
            position: true,
            classYear: true,
            ncaaId: true,
            emailVerified: true,
          },
        }),
        rawPrisma.deal.findMany({
          where: { athleteId: input.athleteId },
          orderBy: { createdAt: 'desc' },
          select: {
            id: true,
            title: true,
            brandName: true,
            amount: true,
            currency: true,
            status: true,
            startDate: true,
            endDate: true,
            createdAt: true,
            createdByAgentId: true,
            commissionPercent: true,
            signedDate: true,
          },
        }),
      ]);
      if (!athlete) throw new ORPCError('NOT_FOUND', { message: 'Athlete not found' });

      const memberRate = member.commissionPercent;
      const contractStart = member.contractStartDate;
      // biome-ignore lint/suspicious/noExplicitAny: prisma row shape
      const dealRows = deals.map((d: any) => {
        // Deals struck before representation began are shown for context
        // but must not count toward the agent's value or commission.
        const inWindow = isWithinContractWindow(d, contractStart);
        return {
          id: d.id,
          title: d.title,
          brandName: d.brandName,
          amount: Number(d.amount),
          currency: d.currency,
          status: d.status,
          startDate: d.startDate?.toISOString() ?? null,
          endDate: d.endDate?.toISOString() ?? null,
          signedDate: d.signedDate?.toISOString() ?? null,
          createdAt: d.createdAt.toISOString(),
          // D9: agent-created and still unconfirmed by the athlete.
          awaitingConfirmation: isAwaitingAthleteConfirmation(d),
          createdByAgent: Boolean(d.createdByAgentId),
          inContractWindow: inWindow,
          commissionPercent: resolveCommissionPercent(d.commissionPercent, memberRate),
          // Zero out earnings for out-of-window deals so the row's own
          // number agrees with the tiles rather than contradicting them.
          commissionEarned: inWindow
            ? commissionEarned(Number(d.amount), d.commissionPercent, memberRate)
            : 0,
        };
      });
      const countedDeals = dealRows.filter((d: { inContractWindow: boolean }) => d.inContractWindow);

      return {
        athlete: {
          ...athlete,
          status: athlete.emailVerified ? ('active' as const) : ('invited' as const),
        },
        memberCommissionPercent: memberRate,
        deals: dealRows,
        memberContractStartDate: contractStart ? contractStart.toISOString() : null,
        // Tiles count ONLY in-window deals (founder ruling 2026-07-27).
        totals: {
          deals: countedDeals.length,
          value: countedDeals.reduce((s: number, d: { amount: number }) => s + d.amount, 0),
          commission: countedDeals.reduce(
            (s: number, d: { commissionEarned: number }) => s + d.commissionEarned,
            0,
          ),
          // Surfaced so the UI can explain a gap between the list and the
          // tiles instead of the agent wondering why they disagree.
          excludedByContractWindow: dealRows.length - countedDeals.length,
        },
      };
    }),

  /**
   * Set the agency's terms for a rostered athlete: the default commission
   * rate and when representation began.
   *
   * Both fields are independently optional — omit one to leave it alone,
   * pass null to clear it. `contractStartDate: null` means "count every
   * deal", which is the correct default for an agency that hasn't
   * recorded a contract date.
   */
  setAthleteTerms: protectedProcedure
    .input(
      z.object({
        orgId: z.string(),
        athleteId: z.string(),
        commissionPercent: z.number().min(0).max(100).nullable().optional(),
        contractStartDate: z.string().date().or(z.string().datetime()).nullable().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const { member } = await requireRosteredAthlete(
        db,
        session.user.id,
        input.orgId,
        input.athleteId,
      );
      const { prisma: rawPrisma } = await import('@app/db');

      // Build the patch from only the keys the caller actually sent, so
      // saving a commission rate can't silently wipe the contract date.
      const data: Record<string, unknown> = {};
      if (Object.hasOwn(input, 'commissionPercent')) {
        data.commissionPercent = input.commissionPercent ?? null;
      }
      if (Object.hasOwn(input, 'contractStartDate')) {
        data.contractStartDate = input.contractStartDate
          ? new Date(input.contractStartDate)
          : null;
      }
      if (Object.keys(data).length === 0) return { ok: true, unchanged: true as const };

      await rawPrisma.member.update({ where: { id: member.id }, data });
      return { ok: true };
    }),

  /**
   * Create a deal ON BEHALF of a rostered athlete (D9).
   *
   * Always lands as a DRAFT stamped with `createdByAgentId`, and the
   * athlete is notified to review and confirm. The agent cannot create an
   * already-live deal and cannot confirm one — that gate is the whole
   * point of the draft model.
   *
   * `organizationId` is forced to the agency (D2) so agency-shared payors
   * resolve for both parties. Note this means agent-created deals carry
   * the AGENCY org, not a university one; agent visibility never derives
   * from that column anyway (it derives from roster membership), so no
   * university compliance view is affected.
   */
  createDealFor: protectedProcedure
    .input(
      z.object({
        orgId: z.string(),
        athleteId: z.string(),
        deal: dealCreateInput,
        commissionPercent: z.number().min(0).max(100).nullable().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const agentId = session.user.id;
      const { agency, member } = await requireRosteredAthlete(
        db,
        agentId,
        input.orgId,
        input.athleteId,
      );

      // Raw prisma: the enhanced client enforces Deal's `auth() == athlete`
      // rule and would reject a write whose athleteId isn't the caller.
      // `requireRosteredAthlete` above is the authorization boundary.
      const { prisma: rawPrisma } = await import('@app/db');

      const effectiveCommission =
        input.commissionPercent !== undefined && input.commissionPercent !== null
          ? input.commissionPercent
          : member.commissionPercent;

      const result = await createDealCore({
        db: rawPrisma,
        actorId: agentId,
        athleteId: input.athleteId,
        createdByAgentId: agentId,
        commissionPercent: effectiveCommission,
        input: {
          ...input.deal,
          // Force DRAFT regardless of what the client sent — the athlete
          // confirms before anything counts as live.
          status: 'DRAFT',
          organizationId: input.orgId,
          source: input.deal.source ?? 'agent',
        },
      });

      await writeAuditLog({
        action: 'agency.deal_created_on_behalf',
        category: 'agency',
        actor: {
          id: agentId,
          name: session.user.name,
          email: session.user.email,
          role: session.user.role ?? null,
        },
        target: { type: 'deal', id: result.id, name: input.deal.title },
        context: {
          organizationId: input.orgId,
          organizationName: agency.name,
          athleteId: input.athleteId,
          commissionPercent: effectiveCommission,
        },
      });

      // Notify the athlete — under the draft-confirm model this is the
      // only way they learn something is waiting for them. Best-effort:
      // the deal exists either way, and it'll surface on their deal list.
      try {
        const { createId } = await import('@app/db');
        const athlete = await rawPrisma.user.findUnique({
          where: { id: input.athleteId },
          select: { email: true },
        });
        const title = `${agency.name} added a deal for you to review`;
        const body = [
          `"${input.deal.title}" with ${input.deal.brandName} was added to your account by your agency.`,
          'It stays a draft until you review and confirm it — nothing is live yet.',
        ].join('\n\n');
        const actionUrl = `/deals/${result.id}`;

        await rawPrisma.notification.create({
          data: {
            id: createId(),
            userId: input.athleteId,
            type: 'system',
            title,
            body,
            actionUrl,
          },
        });

        if (athlete?.email) {
          const { sendNotification } = await import('@app/notifications');
          const appUrl =
            process.env.BETTER_AUTH_URL ?? process.env.VITE_APP_URL ?? 'https://www.nilboard.app';
          await sendNotification({
            userId: input.athleteId,
            title,
            body: `${body}\n\nReview it: ${appUrl.replace(/\/$/, '')}${actionUrl}`,
            channels: ['email'],
            email: athlete.email,
          });
        }
      } catch (e) {
        console.error('[agency.createDealFor] notify failed:', e);
      }

      return { id: result.id, status: 'DRAFT' as const };
    }),

  // -------------------------------------------------------------------------
  // Recruiting dataset (agent epic PR 3)
  //
  // THE paid unlock (amended D7): `searchDataset` / `getDatasetAthlete` /
  // `importProspect` sit behind `requireFeature('recruitingDataset')` —
  // agent plans + admin bypass. Everything else in this router, including
  // the prospect pipeline below, stays free: an agent who imported
  // prospects while subscribed must not lose access to their own pipeline
  // when a card lapses.
  // -------------------------------------------------------------------------

  /** Browse the enriched athlete dataset. Filters are delegated to the
   * provider (server-side at usefoundry for the api provider) — never
   * post-filtered in JS here; that's the brand-directory landmine. */
  searchDataset: protectedProcedure
    .use(requireFeature('recruitingDataset'))
    .input(
      z.object({
        query: z.string().max(200).optional(),
        sport: z.string().max(80).optional(),
        school: z.string().max(120).optional(),
        conference: z.string().max(80).optional(),
        division: z.string().max(20).optional(),
        classYear: z.string().max(20).optional(),
        state: z.string().max(2).optional(),
        followersMin: z.number().int().nonnegative().optional(),
        valuationMin: z.number().int().nonnegative().optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(50).default(20),
      }),
    )
    .handler(async ({ input }) => {
      const { getFoundryProvider } = await import('@app/foundry-athletes');
      const provider = getFoundryProvider();
      const result = await provider.searchAthletes(input);
      return { ...result, provider: provider.id };
    }),

  /** The dataset's real filter value space (sports, class years,
   * divisions that actually have athletes). Cached upstream for 6h — the
   * dataset's rebuild cycle — so the dropdowns always match the data
   * without a hardcoded list drifting stale. */
  datasetFacets: protectedProcedure
    .use(requireFeature('recruitingDataset'))
    .handler(async () => {
      const { getFoundryProvider } = await import('@app/foundry-athletes');
      return getFoundryProvider().getFacets();
    }),

  /** One dataset record, plus whether the caller's agency already imported
   * it (drives the Import vs Already-imported button state). */
  getDatasetAthlete: protectedProcedure
    .use(requireFeature('recruitingDataset'))
    .input(z.object({ foundryId: z.string(), orgId: z.string().optional() }))
    .handler(async ({ context, input }) => {
      const { getFoundryProvider } = await import('@app/foundry-athletes');
      const athlete = await getFoundryProvider().getAthlete(input.foundryId);
      if (!athlete) {
        throw new ORPCError('NOT_FOUND', { message: 'Athlete not found in the dataset.' });
      }
      let existingProspectId: string | null = null;
      if (input.orgId) {
        await requireAgencyAccess(context.db, context.session.user.id, input.orgId);
        const { prisma: rawPrisma } = await import('@app/db');
        const existing = await rawPrisma.athleteProspect.findUnique({
          where: {
            organizationId_foundryAthleteId: {
              organizationId: input.orgId,
              foundryAthleteId: input.foundryId,
            },
          },
          select: { id: true },
        });
        existingProspectId = existing?.id ?? null;
      }
      return { athlete, existingProspectId };
    }),

  /** Import a dataset athlete onto the agency's pipeline. Snapshots the
   * full record into `payload` so nothing downstream (invite prefill,
   * future stat refresh) depends on foundry availability again.
   * Idempotent per (org, foundryId) — re-importing refreshes the
   * snapshot and un-archives. */
  importProspect: protectedProcedure
    .use(requireFeature('recruitingDataset'))
    .input(z.object({ orgId: z.string(), foundryId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const agency = await requireAgencyAccess(db, session.user.id, input.orgId);

      const { getFoundryProvider } = await import('@app/foundry-athletes');
      const athlete = await getFoundryProvider().getAthlete(input.foundryId);
      if (!athlete) {
        throw new ORPCError('NOT_FOUND', { message: 'Athlete not found in the dataset.' });
      }

      const { prisma: rawPrisma, createId } = await import('@app/db');
      const snapshot = {
        source: 'foundry' as const,
        foundryAthleteId: athlete.foundryId,
        payload: JSON.stringify(athlete),
        name: athlete.name,
        email: athlete.email,
        sport: athlete.sport,
        school: athlete.school,
      };
      const prospect = await rawPrisma.athleteProspect.upsert({
        where: {
          organizationId_foundryAthleteId: {
            organizationId: input.orgId,
            foundryAthleteId: input.foundryId,
          },
        },
        create: {
          id: createId(),
          organizationId: input.orgId,
          createdById: session.user.id,
          status: 'prospect',
          ...snapshot,
        },
        // Refresh the snapshot; un-archive; but never regress an
        // 'invited' prospect back to 'prospect' — the invite happened.
        update: { payload: snapshot.payload, sport: snapshot.sport, school: snapshot.school },
      });
      if (prospect.status === 'archived') {
        await rawPrisma.athleteProspect.update({
          where: { id: prospect.id },
          data: { status: 'prospect' },
        });
      }

      await writeAuditLog({
        action: 'agency.prospect_imported',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'athleteProspect', id: prospect.id, name: athlete.name },
        context: { orgId: input.orgId, agency: agency.name, foundryId: input.foundryId },
      });

      return { prospectId: prospect.id };
    }),

  /** The agency's prospect pipeline. NOT plan-gated (see section note). */
  listProspects: protectedProcedure
    .input(z.object({ orgId: z.string() }))
    .handler(async ({ context, input }) => {
      await requireAgencyAccess(context.db, context.session.user.id, input.orgId);
      const { prisma: rawPrisma } = await import('@app/db');
      const rows = await rawPrisma.athleteProspect.findMany({
        where: { organizationId: input.orgId, status: { not: 'archived' } },
        orderBy: { createdAt: 'desc' },
      });
      // biome-ignore lint/suspicious/noExplicitAny: prisma row shape
      return rows.map((p: any) => {
        let followers: number | null = null;
        let nilValuation: number | null = null;
        try {
          const snapshot = p.payload ? (JSON.parse(p.payload) as FoundryAthlete) : null;
          followers = snapshot?.socialFollowers ?? null;
          nilValuation = snapshot?.nilValuation ?? null;
        } catch {
          followers = null;
        }
        return {
          id: p.id,
          name: p.name,
          email: p.email,
          sport: p.sport,
          school: p.school,
          status: p.status as 'prospect' | 'invited',
          source: p.source,
          foundryAthleteId: p.foundryAthleteId,
          invitedAt: p.invitedAt ? p.invitedAt.toISOString() : null,
          invitedEmail: p.invitedEmail,
          linkedUserId: p.linkedUserId,
          socialFollowers: followers,
          nilValuation,
          createdAt: p.createdAt.toISOString(),
        };
      });
    }),

  /** Edit a prospect (email before invite, archive to dismiss). */
  updateProspect: protectedProcedure
    .input(
      z.object({
        orgId: z.string(),
        prospectId: z.string(),
        email: z.string().email().nullable().optional(),
        status: z.enum(['prospect', 'archived']).optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      await requireAgencyAccess(context.db, context.session.user.id, input.orgId);
      const { prisma: rawPrisma } = await import('@app/db');
      const prospect = await rawPrisma.athleteProspect.findUnique({
        where: { id: input.prospectId },
      });
      if (!prospect || prospect.organizationId !== input.orgId) {
        throw new ORPCError('NOT_FOUND', { message: 'Prospect not found.' });
      }
      const data: Record<string, unknown> = {};
      if (input.email !== undefined) data.email = input.email;
      if (input.status !== undefined) data.status = input.status;
      if (Object.keys(data).length === 0) return { id: prospect.id };
      await rawPrisma.athleteProspect.update({ where: { id: prospect.id }, data });
      return { id: prospect.id };
    }),

  /** Close a prospect: run the standard org invite (stub User + one-click
   * magic link + notification + email), then prefill the User row from the
   * dataset snapshot per the field-class merge — volatile stats always
   * take the dataset value, identity fields fill empty gaps only. */
  inviteProspect: protectedProcedure
    .input(
      z.object({
        orgId: z.string(),
        prospectId: z.string(),
        /** Override the dataset email — the agent confirms the real
         * address before anything is sent. Persisted onto the prospect. */
        email: z.string().email().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const agency = await requireAgencyAccess(db, session.user.id, input.orgId);
      const { prisma: rawPrisma } = await import('@app/db');

      const prospect = await rawPrisma.athleteProspect.findUnique({
        where: { id: input.prospectId },
      });
      if (!prospect || prospect.organizationId !== input.orgId) {
        throw new ORPCError('NOT_FOUND', { message: 'Prospect not found.' });
      }
      if (prospect.status === 'archived') {
        throw new ORPCError('BAD_REQUEST', { message: 'Un-archive this prospect before inviting.' });
      }
      const email = (input.email ?? prospect.email ?? '').trim().toLowerCase();
      if (!email) {
        throw new ORPCError('BAD_REQUEST', {
          message: 'This prospect has no email — add one before inviting.',
        });
      }

      // The battle-tested invite path — identical to a roster invite from
      // the roster page. Handles new-vs-resend, CONFLICT for existing
      // verified members, magic-link token, bell + email.
      const invite = await inviteMemberCore(context, { orgId: input.orgId, email, role: 'member' });

      // Prefill from the dataset snapshot (field-class merge). A manual
      // prospect with no payload simply skips this.
      let mergedFields: string[] = [];
      if (prospect.payload) {
        try {
          const dataset = JSON.parse(prospect.payload) as FoundryAthlete;
          const existing = await rawPrisma.user.findUnique({
            where: { id: invite.userId },
            select: {
              name: true, sport: true, position: true, school: true,
              conference: true, division: true, classYear: true, hometown: true,
              instagramHandle: true, tiktokHandle: true, twitterHandle: true, youtubeHandle: true,
              instagramFollowers: true, tiktokFollowers: true, twitterFollowers: true,
              youtubeFollowers: true, socialFollowers: true, socialEngagement: true,
            },
          });
          if (existing) {
            const patch = buildProspectMergePatch(existing, dataset, {
              isNewUser: invite.isNewUser,
            });
            mergedFields = Object.keys(patch);
            if (mergedFields.length > 0) {
              await rawPrisma.user.update({ where: { id: invite.userId }, data: patch });
            }
          }
        } catch (e) {
          // Prefill is best-effort — a bad snapshot must not undo a
          // successful invite. The invite result is already committed.
          console.error('[agency.inviteProspect] prefill merge failed:', e);
        }
      }

      await rawPrisma.athleteProspect.update({
        where: { id: prospect.id },
        data: {
          status: 'invited',
          invitedAt: new Date(),
          invitedEmail: email,
          linkedUserId: invite.userId,
          ...(input.email ? { email } : {}),
        },
      });

      await writeAuditLog({
        action: 'agency.prospect_invited',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'athleteProspect', id: prospect.id, name: prospect.name },
        context: {
          orgId: input.orgId,
          agency: agency.name,
          invitedEmail: email,
          isNewUser: invite.isNewUser,
          isResend: invite.isResend,
          mergedFields,
        },
      });

      return {
        prospectId: prospect.id,
        userId: invite.userId,
        isNewUser: invite.isNewUser,
        isResend: invite.isResend,
        emailSent: invite.emailSent,
        emailError: invite.emailError,
        inviteUrl: invite.inviteUrl,
        mergedFields,
      };
    }),
};
